/**
 * Trial-period UI helpers.
 *
 * Both the superadmin tenant list and the tenant_admin dashboard
 * surface the same "N days left" calculation, so the math + label
 * formatting + severity bucketing live here and the two pages stay
 * in lockstep.
 *
 * "Day" is calendar-day based on the client's local timezone — a
 * trial_ends_at of "tomorrow at 09:00" reads as "1 day left" the
 * moment it crosses midnight tonight. That matches user expectation
 * better than counting raw 24h windows, which would round to "0"
 * during business hours of the same day.
 */

import { formatDate } from './datetime';

/**
 * Returns days remaining as an integer, rounded toward the future:
 *   * positive   → trial still has time left
 *   * 0          → trial ends some time today
 *   * negative   → trial has expired
 * The caller decides what to do at each bucket.
 */
export function trialDaysRemaining(
  trialEndsAt: string,
  now: Date = new Date(),
): number {
  const end = new Date(trialEndsAt);
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.ceil((end.getTime() - now.getTime()) / msPerDay);
}

/**
 * Human-readable trial state for use in a chip / caption.
 *
 *   "Trial: 5 days left" / "Trial: 1 day left" / "Trial: ends today"
 *   "Trial expired DATE"
 */
export function formatTrialLabel(
  trialEndsAt: string,
  now: Date = new Date(),
): string {
  const daysLeft = trialDaysRemaining(trialEndsAt, now);
  if (daysLeft > 1) return `Trial: ${daysLeft} days left`;
  if (daysLeft === 1) return 'Trial: 1 day left';
  if (daysLeft === 0) return 'Trial: ends today';
  return `Trial expired ${formatDate(trialEndsAt)}`;
}

/**
 * MUI Alert severity buckets:
 *   * `info`    — comfortably long (> 7 days)
 *   * `warning` — getting close (1–7 days, inclusive)
 *   * `error`   — today or already past (0 or negative)
 */
export function trialSeverity(
  trialEndsAt: string,
  now: Date = new Date(),
): 'info' | 'warning' | 'error' {
  const daysLeft = trialDaysRemaining(trialEndsAt, now);
  if (daysLeft <= 0) return 'error';
  if (daysLeft <= 7) return 'warning';
  return 'info';
}
